import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { localQuantity } from '@theme/local-baseline';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Shopify money format (`${{amount}}`, `{{amount_with_comma_separator}}`, …).
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = Math.round(Number(cents));
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format?.match(placeholder);
  if (!match || !Number.isFinite(amount)) return '';

  const withDelimiters = (number, precision, thousands, decimal) => {
    const fixed = (number / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return fraction ? grouped + decimal + fraction : grouped;
  };

  /** @type {Record<string, string>} */
  const formats = {
    amount: withDelimiters(amount, 2, ',', '.'),
    amount_no_decimals: withDelimiters(amount, 0, ',', '.'),
    amount_with_comma_separator: withDelimiters(amount, 2, '.', ','),
    amount_no_decimals_with_comma_separator: withDelimiters(amount, 0, '.', ','),
    amount_with_apostrophe_separator: withDelimiters(amount, 2, "'", '.'),
    amount_no_decimals_with_space_separator: withDelimiters(amount, 0, ' ', ''),
    amount_with_space_separator: withDelimiters(amount, 2, ' ', ','),
    amount_with_period_and_space_separator: withDelimiters(amount, 2, ' ', '.'),
  };

  return format.replace(placeholder, formats[match[1]] ?? formats.amount);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Ignore a cart/change response when a newer quantity request is already in flight. */
  #priceRequest = 0;

  /** Ignore a local stock lookup when the rows were re-rendered meanwhile. */
  #shipRequest = 0;

  /**
   * Section morphs reset data-ship-local to the server's "0" and do not reach updatedCallback
   * on this element, so watch the rows directly.
   */
  #shipObserver = new MutationObserver((records) => {
    const touched = records.some(
      (record) =>
        record.type === 'attributes' ||
        [...record.addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches('[data-ship-variant]') || node.querySelector('[data-ship-variant]') !== null)
        )
    );
    if (touched) this.#resolveShipLocal();
  });

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onShipNote);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onPrice);
    this.#shipObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-ship-local', 'data-key'],
    });
    this.#resolveShipLocal();
  }

  /**
   * Rows render data-ship-local="0"; Liquid cannot see per-warehouse stock.
   * Mark each row that the visitor's local warehouse holds, then repaint the note.
   */
  async #resolveShipLocal() {
    const note = this.querySelector('.cart__custom-note');
    if (!(note instanceof HTMLElement) || !note.hasAttribute('data-local-market')) return;

    const request = ++this.#shipRequest;
    const rows = [...(this.refs.cartItemRows ?? [])];
    const results = await Promise.all(
      rows.map((row) => {
        const { shipProduct, shipVariant, shipQty } = row.dataset;
        if (row.dataset.shipCustom === '1' || !shipProduct || !shipVariant) return Promise.resolve(NaN);
        return localQuantity(shipProduct, shipVariant, shipQty).catch(() => NaN);
      })
    );
    if (request !== this.#shipRequest || !this.isConnected) return;

    rows.forEach((row, index) => {
      const qty = results[index];
      const local = Number.isFinite(qty) && qty >= 1 ? '1' : '0';
      if (row.dataset.shipLocal !== local) row.dataset.shipLocal = local;
    });
    this.#paintShipNote();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onShipNote);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onPrice);
    this.#shipObserver.disconnect();
  }

  /**
   * Swap the checkout note as soon as the quantity input changes.
   * The cart section still refreshes prices; this does not wait for that HTML.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onShipNote = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#paintShipNote();
  };

  /**
   * Line price, subtotal, and checkout total update on the click.
   * The debounced cart request only confirms the server total.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onPrice = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#paintPrices();
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, cartItemKey: key } = event.detail;

    // Prefer line-item key: cart rows may render oldest-first while Shopify line
    // numbers stay newest-first. Keyed /cart/change is order-independent.
    if (!key && !line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, key);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });
  }

  /**
   * Finds the cart row. Prefer key: display order is not Shopify line order.
   * @param {number} [line] - The line item number (1-based).
   * @param {string} [key] - Cart line item key.
   */
  #rowForLine(line, key) {
    const rows = this.refs.cartItemRows ?? [];

    if (key) {
      const byKey = rows.find((row) => row.dataset.key === key);
      if (byKey) return byKey;
    }

    if (line) {
      return rows.find((row) => Number(row.dataset.line) === line) ?? rows[line - 1];
    }

    return undefined;
  }

  /**
   * Handles the line item removal.
   * @param {number} [line] - The line item index.
   * @param {string} [key] - Cart line item key.
   */
  onLineItemRemove(line, key) {
    // From `on:click="/onLineItemRemove/<line>"` the second argument is the click event.
    const lineKey = typeof key === 'string' ? key : undefined;
    const cartItemRowToRemove = this.#rowForLine(line, lineKey);
    const removedKey = cartItemRowToRemove?.dataset.key || lineKey;
    this.#paintPrices(removedKey);
    this.#paintShipNote(removedKey);
    this.updateQuantity({
      line,
      key: removedKey,
      quantity: 0,
      action: 'clear',
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line (fallback when key is missing).
   * @param {string} [config.key] - Cart line item key (preferred).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, key, quantity } = config;
    /** @type {Record<string, unknown>} */
    const payload = { quantity };
    // Prefer id=line-key over line=: independent of display order.
    // No `sections`: a quantity change only needs the cart JSON. Rendering the
    // whole drawer HTML is what made the total wait.
    if (typeof key === 'string' && key) payload.id = key;
    else payload.line = line;

    const request = ++this.#priceRequest;
    const body = JSON.stringify(payload);

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        if (request !== this.#priceRequest) return;

        const cart = JSON.parse(responseText);
        if (cart.errors) {
          if (quantity === 0) {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
            return;
          }
          this.#handleCartError(line, key, cart);
          return;
        }

        if (!cart.item_count) {
          this.#publishCart(cart);
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        this.#applyServerCart(cart);
        this.#publishCart(cart);
      })
      .catch((error) => {
        console.error(error);
        if (request !== this.#priceRequest) return;
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {Record<string, any>} cart
   */
  #publishCart(cart) {
    this.dispatchEvent(
      new CartUpdateEvent(cart, this.sectionId, {
        itemCount: Number(cart.item_count) || 0,
        source: 'cart-items-component',
        priceOnly: true,
        cart,
      })
    );
  }

  /**
   * Replace optimistic numbers with the cart JSON. Does not morph the drawer.
   * @param {Record<string, any>} cart
   */
  #applyServerCart(cart) {
    const items = new Map((cart.items || []).map((item) => [item.key, item]));
    const rows = (this.refs.cartItemRows ?? []).filter(
      (row) => row.isConnected && !row.classList.contains('removing')
    );

    // Shopify can re-key a line when its quantity changes (e.g. with a discount code applied).
    // A stale key is not a deleted line, so re-render instead of dropping the row.
    if (rows.some((row) => !items.has(row.dataset.key || ''))) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    for (const row of rows) {
      const item = items.get(row.dataset.key || '');
      if (!item) continue;

      row.dataset.unitFinal = String(item.final_price);
      const input = row.querySelector('[ref="quantityInput"]');
      if (input instanceof HTMLInputElement) {
        input.value = String(item.quantity);
        input.defaultValue = String(item.quantity);
      }
      this.#setLinePrice(row, Number(item.final_line_price), Number(item.quantity));
    }

    this.#setCartTotals(Number(cart.total_price), Number(cart.item_count) || 0);
    this.#setOrderSaving(Number(cart.total_discount) || 0);
  }

  /**
   * @param {string} [excludeKey] Line being removed; its input may still show the old quantity.
   */
  #paintPrices(excludeKey) {
    const rows = this.refs.cartItemRows ?? [];
    let total = 0;
    let count = 0;

    for (const row of rows) {
      if (!row.isConnected || row.classList.contains('removing')) continue;
      if (excludeKey && (row.dataset.key === excludeKey || row.dataset.parentKey === excludeKey)) continue;

      const input = row.querySelector('[ref="quantityInput"]');
      const qty = input instanceof HTMLInputElement ? Number(input.value) : NaN;
      if (!Number.isFinite(qty) || qty <= 0) continue;

      const unit = Number(row.dataset.unitFinal);
      if (!Number.isFinite(unit)) return;

      const line = unit * qty;
      total += line;
      count += qty;
      this.#setLinePrice(row, line, qty);
    }

    this.#setCartTotals(total, count);
  }

  /**
   * @param {HTMLTableRowElement} row
   * @param {number} lineCents
   * @param {number} qty
   */
  #setLinePrice(row, lineCents, qty) {
    const price = row.querySelector('text-component');
    const formatted = formatMoney(lineCents, this.dataset.lineMoneyFormat || '');
    if (price && formatted) {
      price.innerHTML = formatted;
      price.setAttribute('value', price.textContent.trim());
    }

    const savings = row.querySelector('.cart-items__savings');
    if (!(savings instanceof HTMLElement)) return;

    const compare = Number(row.dataset.unitCompare);
    const saved = Number.isFinite(compare) ? compare * qty - lineCents : 0;
    const amount = formatMoney(saved, this.dataset.saveMoneyFormat || this.dataset.lineMoneyFormat || '');
    const template = this.dataset.saveTemplate || '';
    if (saved > 0 && amount && template.includes('__AMOUNT__')) {
      savings.hidden = false;
      savings.innerHTML = template.replace('__AMOUNT__', amount);
    } else if (saved <= 0) {
      savings.hidden = true;
    }
  }

  /**
   * @param {number} totalCents
   * @param {number} count
   */
  #setCartTotals(totalCents, count) {
    const formatted = formatMoney(totalCents, this.dataset.totalMoneyFormat || '');
    const total = this.querySelector('[ref="cartTotal"]');
    if (total && formatted) {
      total.innerHTML = formatted;
      total.setAttribute('value', total.textContent.trim());
    }

    const checkout = this.querySelector('.cart__checkout-button-label');
    const prefix = checkout instanceof HTMLElement ? checkout.dataset.checkoutPrefix : '';
    if (checkout && prefix && formatted) checkout.innerHTML = `${prefix} - ${formatted}`;

    const hiddenCount = this.querySelector('[ref="cartItemCount"]');
    if (hiddenCount) hiddenCount.textContent = String(count);

    const bubble = this.querySelector('.cart-drawer__heading [ref="cartBubbleCount"]');
    if (bubble) bubble.textContent = count < 100 ? String(count) : '';
  }

  /**
   * @param {number} discountCents
   */
  #setOrderSaving(discountCents) {
    const value = this.querySelector('.cart-drawer-summary__saving-value');
    const row = value?.closest('.cart-drawer-summary__saving');
    if (!(value instanceof HTMLElement) || !(row instanceof HTMLElement)) return;

    if (discountCents > 0) {
      const formatted = formatMoney(discountCents, this.dataset.saveMoneyFormat || '');
      if (formatted) value.innerHTML = `-${formatted}`;
      row.hidden = false;
      return;
    }

    row.hidden = true;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} [line] - The line.
   * @param {string} [key] - Cart line item key.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, key, parsedResponseText) => {
    // Rows are not in line order; resolve by key first, then line.
    const quantitySelector =
      this.#rowForLine(line, key)?.querySelector('quantity-selector-component') ??
      (line ? this.refs.quantitySelectors[line - 1] : undefined);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const errorLine = line || Number(quantityInput.dataset.cartLine);
    const cartItemError = this.refs[`cartItemError-${errorLine}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${errorLine}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
    this.#paintPrices();
    this.#paintShipNote();
  }

  /**
   * Checkout note from the rows already on the page.
   * Customization wins. Otherwise every remaining line must be in the local warehouse.
   * @param {string} [excludeKey] - Line being removed; its quantity input may still show the old value.
   */
  #paintShipNote(excludeKey) {
    const note = this.querySelector('.cart__custom-note');
    if (!(note instanceof HTMLElement)) return;

    const rows = this.refs.cartItemRows ?? [];
    let custom = false;
    let local = note.hasAttribute('data-local-market');
    let count = 0;

    for (const row of rows) {
      if (row.classList.contains('removing')) continue;
      if (excludeKey && (row.dataset.key === excludeKey || row.dataset.parentKey === excludeKey)) continue;
      const input = row.querySelector('[ref="quantityInput"]');
      const qty = input instanceof HTMLInputElement ? Number(input.value) : 1;
      if (!Number.isFinite(qty) || qty <= 0) continue;
      count += 1;
      if (row.dataset.shipCustom === '1') {
        custom = true;
        local = false;
        break;
      }
      if (row.dataset.shipLocal !== '1') local = false;
    }

    if (count === 0) local = false;

    const text = custom ? note.dataset.customShip || '' : local ? note.dataset.localShip || '' : '';
    if (!text) {
      note.hidden = true;
      note.textContent = '';
      return;
    }
    note.hidden = false;
    if (note.textContent !== text) note.textContent = text;
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.detail?.data?.priceOnly) {
      if (event.target !== this && event.detail.data.cart) {
        if (!event.detail.data.itemCount) sectionRenderer.renderSection(this.sectionId, { cache: false });
        else this.#applyServerCart(event.detail.data.cart);
      }
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
